import { toast } from "react-toastify";
import React from "react";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { parseDateOnlyAsLocal } from "../../utilities/CommonUtilities";

function CustomDatePicker({
  setDateFilter,
  filterDate,
  isLoading,
  start_date_limit,
  end_date_limit,
  // When set, the class owns the wrapper's look (desktop desk chrome).
  className,
  dateFormat = "MM/dd/yyyy",
  showIcon = false,
}) {
  const MAX_RANGE_YEARS = 6;
  const MIN_RANGE_DAYS = 7;

  const clampToDayStart = (d) => {
    const nd = new Date(d);
    nd.setHours(0, 0, 0, 0);
    return nd;
  };

  const coerceDate = (d) => {
    if (!d) return null;
    return clampToDayStart(parseDateOnlyAsLocal(d));
  };

  const addDays = (date, days) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return clampToDayStart(d);
  };
  const subDays = (date, days) => addDays(date, -days);

  const todayDate = clampToDayStart(new Date());
  const minDate = clampToDayStart(
    parseDateOnlyAsLocal(start_date_limit || "2016-01-01")
  );
  const maxDate = clampToDayStart(
    parseDateOnlyAsLocal(end_date_limit || todayDate)
  );

  const startDate = coerceDate(filterDate.startDate) ?? todayDate;
  const endDate = coerceDate(filterDate.endDate) ?? todayDate;

  const addYears = (date, years) => {
    const d = new Date(date);
    d.setFullYear(d.getFullYear() + years);
    return clampToDayStart(d);
  };
  const subYears = (date, years) => {
    const d = new Date(date);
    d.setFullYear(d.getFullYear() - years);
    return clampToDayStart(d);
  };

  const enforceMaxRangeFromStart = (s, e) => {
    const maxEnd = addYears(s, MAX_RANGE_YEARS);
    const newEnd = e > maxEnd ? maxEnd : e;
    return newEnd > maxDate ? maxDate : newEnd;
  };
  const enforceMaxRangeFromEnd = (s, e) => {
    const minStart = subYears(e, MAX_RANGE_YEARS);
    const newStart = s < minStart ? minStart : s;
    return newStart < minDate ? minDate : newStart;
  };

  const earliestDate = (...dates) =>
    dates.reduce((earliest, date) => (date < earliest ? date : earliest));
  const latestDate = (...dates) =>
    dates.reduce((latest, date) => (date > latest ? date : latest));

  const defaultStartPickerMax = subDays(todayDate, MIN_RANGE_DAYS);
  const startPickerMax = earliestDate(
    maxDate,
    defaultStartPickerMax,
    subDays(endDate, MIN_RANGE_DAYS)
  );
  const endPickerMin = latestDate(
    minDate,
    addDays(startDate, MIN_RANGE_DAYS)
  );

  const handleStartDateChange = (date) => {
    if (!date) return toast.error("Please select a start date.");
    const selected = clampToDayStart(date);

    if (selected > todayDate) {
      return toast.error("Start date cannot be in the future.");
    }
    if (selected < minDate) {
      return toast.error(
        `Start date cannot be before ${minDate.toDateString()}.`
      );
    }
    if (selected > maxDate) {
      return toast.error(
        `Start date cannot be after ${maxDate.toDateString()}.`
      );
    }
    if (selected > defaultStartPickerMax) {
      return toast.error(
        `Start date must be at least ${MIN_RANGE_DAYS} days before today.`
      );
    }
    if (selected > subDays(endDate, MIN_RANGE_DAYS)) {
      return toast.error(
        `Start date must be at least ${MIN_RANGE_DAYS} days before end date.`
      );
    }

    // Keep the configured max-year window from start.
    if (endDate > addYears(selected, MAX_RANGE_YEARS)) {
      const adjustedEnd = enforceMaxRangeFromStart(selected, endDate);
      setDateFilter((p) => ({
        ...p,
        startDate: selected,
        endDate: adjustedEnd,
      }));
      toast.info(
        `Adjusted end date to keep the range within ${MAX_RANGE_YEARS} years.`
      );
      return;
    }

    setDateFilter((p) => ({ ...p, startDate: selected }));
    toast.success("Start date updated.");
  };

  const handleEndDateChange = (date) => {
    if (!date) return toast.error("Please select an end date.");
    const selected = clampToDayStart(date);

    if (selected > maxDate) {
      return toast.error(
        `End date cannot be after ${maxDate.toDateString()}.`
      );
    }
    if (selected < minDate) {
      return toast.error(
        `End date cannot be before ${minDate.toDateString()}.`
      );
    }
    if (selected < endPickerMin) {
      toast.error(
        `End date must be at least ${MIN_RANGE_DAYS} days after start date.`
      );
      return;
    }

    // Keep the configured max-year window from end.
    if (startDate < subYears(selected, MAX_RANGE_YEARS)) {
      const adjustedStart = enforceMaxRangeFromEnd(startDate, selected);
      setDateFilter((p) => ({
        ...p,
        startDate: adjustedStart,
        endDate: selected,
      }));
      toast.info(
        `Adjusted start date to keep the range within ${MAX_RANGE_YEARS} years.`
      );
      return;
    }

    setDateFilter((p) => ({ ...p, endDate: selected }));
    toast.success("End date updated.");
  };

  return (
    <div
      className={
        className ||
        "d-flex align-items-center border border-2 py-1 px-2 bg-white rounded mx-2 z-n1"
      }
    >
      {showIcon ? (
        <i className="fa-regular fa-calendar" aria-hidden="true" />
      ) : null}
      <DatePicker
        selected={startDate}
        onChange={handleStartDateChange}
        maxDate={startPickerMax}
        minDate={minDate}
        dateFormat={dateFormat}
        className="col-12 border-1 rounded"
        placeholderText="Start Date"
        disabled={isLoading}
        strictParsing
      />
      <div className="mx-2">
        <i className="fa-solid fa-arrow-right" aria-hidden="true" />
      </div>
      <DatePicker
        selected={endDate}
        onChange={handleEndDateChange}
        minDate={endPickerMin}
        maxDate={maxDate}
        dateFormat={dateFormat}
        className="col-12 border-1 rounded"
        placeholderText="End Date"
        disabled={isLoading}
        strictParsing
      />
    </div>
  );
}

export default CustomDatePicker;
